'use client';

import { forwardRef, InputHTMLAttributes, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  required?: boolean;
  showPasswordToggle?: boolean;
  helperText?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, required = false, showPasswordToggle = false, helperText, className = '', type, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
      setShowPassword(prev => !prev);
    };

    // パスワード表示切り替えがある場合、typeを動的に変更
    const inputType = showPasswordToggle && showPassword ? 'text' : type;

    return (
      <div className="flex flex-col gap-2 w-full">
        {label && (
          <div className="flex gap-0.5 items-start leading-[1.4]">
            <label htmlFor={props.id} className="text-sm font-bold text-mono-100 font-noto-sans-jp">
              {label}
            </label>
            {required && <span className="text-sm font-semibold text-secondary-80">*</span>}
          </div>
        )}
        <div className="relative">
          <input
            ref={ref}
            {...props}
            type={inputType}
            required={required}
            className={`
              w-full h-[46px] px-3 py-3 ${showPasswordToggle ? 'pr-12' : ''}
              border border-mono-50 rounded-[4px]
              bg-mono-0
              text-base text-mono-100 font-noto-sans-jp leading-[1.4]
              placeholder:text-mono-40
              focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
              disabled:bg-gray-100 disabled:cursor-not-allowed
              ${error ? 'border-red-500' : ''}
              ${className}
            `}
          />
          {showPasswordToggle && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-mono-100"
              aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
            >
              {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          )}
        </div>
        {helperText && (
          <p className="text-sm text-mono-60 font-noto-sans-jp leading-[1.4]">
            {helperText}
          </p>
        )}
        {error && <p className="text-sm text-red-500 font-noto-sans-jp leading-[1.4]">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
